import { MaybeNil } from '../../../../utils/interfaces';
import { useApolloClient } from '@apollo/client';
import { useCallback, useRef, useState } from 'react';
import {
  IComputeAbsoluteTimePeriodQuery,
  IComputeAbsoluteTimePeriodQueryVariables,
  IDatePickerFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { GraphQLLocalDate } from '../../../../utils/types';
import { IDateInput } from '../../../../redux/slices/oldBuilder/date/types';
import { COMPUTE_ABSOLUTE_TIME_PERIOD } from '../../../../utils/apollo/datePicker/queries';
import { toDatePickerInput } from '../../utils/builderUtils';
import { freeze } from '@reduxjs/toolkit';

const useComputeAbsoluteTimePeriod = (schemaId: MaybeNil<string>) => {
  const client = useApolloClient();
  const [loading, setLoading] = useState(false);

  const cache = useRef(new Map<string, IDateInput>());

  const computeAbsoluteTimePeriod = useCallback(
    async (
      datePicker: IDatePickerFieldsFragment,
      endDate: GraphQLLocalDate,
    ): Promise<IDateInput> => {
      // some date ranges can be calculated directly w/o calling Kobol
      // this is just an optimization to avoid calling Kobol
      if (datePicker.__typename === 'DatePickerFixed') {
        return {
          startDate: datePicker.start,
          endDate: datePicker.end,
          datePicker,
        };
      } else if (datePicker.__typename === 'DatePickerSince') {
        return {
          startDate: datePicker.start,
          endDate,
          datePicker,
        };
      }

      const input: IComputeAbsoluteTimePeriodQueryVariables['input'] = {
        datePicker: toDatePickerInput(datePicker),
        endDate,
        schemaId: schemaId ?? null,
      };

      // Another optimization: cache the computed values from Kobol
      // Reduces the number of times a spinner appears in the UI
      const cacheKey = JSON.stringify(input);
      const cached = cache.current.get(cacheKey);
      if (cached) {
        return cached;
      }

      // the rest of date pickers require Kobol help
      setLoading(true);
      const { data } = await client.query<
        IComputeAbsoluteTimePeriodQuery,
        IComputeAbsoluteTimePeriodQueryVariables
      >({
        query: COMPUTE_ABSOLUTE_TIME_PERIOD,
        variables: {
          input,
        },
      });
      setLoading(false);
      const { start, end } = data.absoluteTimePeriodCompute;
      const result = freeze<IDateInput>({
        startDate: start,
        endDate: end,
        datePicker,
      });
      cache.current.set(cacheKey, result);
      return result;
    },
    [client, schemaId],
  );

  return { computeAtpLoading: loading, computeAbsoluteTimePeriod };
};

export default useComputeAbsoluteTimePeriod;
